import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useStableAuth } from './useStableAuth';
import { toast } from 'sonner';
import type { Task, TaskFormData, TaskFilters, TaskStats, TaskStatus } from '@/types/task';
import { projectTaskKeys } from './useProjectTasks';

// Query keys for cache management
export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (filters?: TaskFilters) => [...taskKeys.lists(), filters] as const,
  stats: (userId?: string) => [...taskKeys.all, 'stats', userId] as const,
};

// Key root for the infinite / Kanban query (useInfiniteTasks)
export const infiniteTaskKeys = {
  all: ['infinite-tasks'] as const,
};

export const useTasks = (filters?: TaskFilters) => {
  const { userId, companyId } = useStableAuth();
  const queryClient = useQueryClient();

  // Fetch tasks query
  const {
    data: tasks = [],
    isLoading: loading,
    refetch
  } = useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: async () => {
      if (!userId || !companyId) return [];

      let query = supabase
        .from('tasks')
        .select(`
          *,
          section:task_sections(*),
          task_project_memberships!task_project_memberships_task_id_fkey(
            project:projects!task_project_memberships_project_id_fkey(id, name)
          )
        `)
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status && filters.status.length > 0) {
        query = query.in('status', filters.status);
      }
      if (filters?.priority && filters.priority.length > 0) {
        query = query.in('priority', filters.priority);
      }
      
      // user_tasks filter: assigned_to OR created_by (for unified "my tasks" logic)
      // Tarefas normais (não-sistema): assigned_to OU created_by
      // Tarefas de sistema: APENAS assigned_to (não poluir inbox do criador)
      // PostgREST não suporta AND aninhado em OR de forma confiável —
      // usamos OR simples e filtramos is_system_generated no cliente.
      if (filters?.user_tasks) {
        query = query.or(
          `assigned_to.eq.${filters.user_tasks},and(created_by.eq.${filters.user_tasks},is_system_generated.eq.false),and(created_by.eq.${filters.user_tasks},is_system_generated.is.null)`
        );
      } else {
        if (filters?.assigned_to) {
          query = query.eq('assigned_to', filters.assigned_to);
        }
        if (filters?.created_by) {
          query = query.eq('created_by', filters.created_by);
        }
      }
      
      if (filters?.section_id) {
        query = query.eq('section_id', filters.section_id);
      }
      if (filters?.is_private !== undefined) {
        query = query.eq('is_private', filters.is_private);
      }
      if (filters?.overdue) {
        query = query.lt('due_date', new Date().toISOString().split('T')[0])
                     .neq('status', 'completed');
      }
      if (filters?.today) {
        const today = new Date().toISOString().split('T')[0];
        query = query.eq('due_date', today).neq('status', 'completed');
      }
      if (filters?.this_week) {
        const today = new Date();
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        query = query.gte('due_date', today.toISOString().split('T')[0])
                     .lte('due_date', nextWeek.toISOString().split('T')[0])
                     .neq('status', 'completed');
      }
      if (filters?.search) {
        query = query.ilike('title', `%${filters.search}%`);
      }
      if (filters?.date_range?.start) {
        query = query.gte('due_date', filters.date_range.start);
      }
      if (filters?.date_range?.end) {
        query = query.lte('due_date', filters.date_range.end);
      }

      query = query.limit(500);
      const { data, error } = await query;

      if (error) throw error;
      // @ts-ignore - Supabase type mismatch on enum columns
      return (data || []) as Task[];
    },
    enabled: !!userId && !!companyId,
  });

  // Fetch stats query
  const { data: stats = null } = useQuery({
    queryKey: taskKeys.stats(userId),
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase.rpc('get_user_task_stats', {
        user_uuid: userId
      });

      if (error) throw error;
      if (data && data.length > 0) {
        return data[0] as TaskStats;
      }
      return null;
    },
    enabled: !!userId,
  });

  // Helper to invalidate all task-related queries (includes infinite/Kanban)
  const invalidateAllTasks = () => {
    queryClient.invalidateQueries({ queryKey: taskKeys.all });
    queryClient.invalidateQueries({ queryKey: infiniteTaskKeys.all });
    queryClient.invalidateQueries({ queryKey: projectTaskKeys.all });
    queryClient.invalidateQueries({ queryKey: ['task'] });
    queryClient.invalidateQueries({ queryKey: ['subtasks'] });
  };

  // Create task mutation
  const createTaskMutation = useMutation({
    mutationFn: async (taskData: TaskFormData) => {
      if (!userId || !companyId) throw new Error('User not authenticated');

      const sanitizedData = {
        ...taskData,
        assigned_to: taskData.assigned_to || userId,
        section_id: taskData.section_id || undefined,
        parent_task_id: taskData.parent_task_id || undefined,
        company_id: companyId,
        created_by: userId
      };

      const { data, error } = await supabase
        .from('tasks')
        .insert([sanitizedData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Tarefa criada com sucesso');
      invalidateAllTasks();
    },
    onError: (error: any) => {
      console.error('❌ ERRO ao criar tarefa:', error);
      toast.error(`Erro ao criar tarefa: ${error.message || 'Erro desconhecido'}`);
    }
  });

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, updates }: { taskId: string; updates: Partial<TaskFormData> }) => {
      const { error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', taskId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Tarefa atualizada com sucesso');
      invalidateAllTasks();
    },
    onError: (error: any) => {
      console.error('❌ ERRO ao atualizar tarefa:', error);
      if (error.message?.includes('read-only')) {
        toast.error('Banco de dados temporariamente indisponível. Tente novamente em alguns segundos.');
      } else {
        toast.error(`Erro ao atualizar tarefa: ${error.message || 'Erro desconhecido'}`);
      }
    }
  });

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      if (!userId) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('tasks')
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: userId
        })
        .eq('id', taskId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Tarefa excluída com sucesso');
      invalidateAllTasks();
    },
    onError: (error: any) => {
      console.error('Erro ao excluir tarefa:', error);
      toast.error('Erro ao excluir tarefa');
    }
  });

  // Complete task mutation
  const completeTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      if (!userId) throw new Error('User not authenticated');

      const { data, error } = await supabase.rpc('complete_task', {
        p_task_id: taskId,
        p_actor_id: userId
      });

      if (error) throw error;
      return data;
    },
    onMutate: async (taskId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: taskKeys.all });

      // Snapshot previous value
      const previousTasks = queryClient.getQueryData(taskKeys.list(filters));

      // Optimistically update
      queryClient.setQueryData(taskKeys.list(filters), (old: Task[] | undefined) => 
        old?.map(task => 
          task.id === taskId 
            ? { ...task, status: 'completed' as TaskStatus, completed_at: new Date().toISOString() }
            : task
        ) || []
      );

      return { previousTasks };
    },
    onSuccess: () => {
      toast.success('Tarefa concluída');
      invalidateAllTasks();
    },
    onError: (error: any, _, context) => {
      console.error('Erro ao concluir tarefa:', error);
      toast.error('Erro ao concluir tarefa');
      // Rollback on error
      if (context?.previousTasks) {
        queryClient.setQueryData(taskKeys.list(filters), context.previousTasks);
      }
    }
  });

  // Assign task mutation
  const assignTaskMutation = useMutation({
    mutationFn: async ({ taskId, newAssigneeId }: { taskId: string; newAssigneeId: string }) => {
      if (!userId) throw new Error('User not authenticated');

      const { data, error } = await supabase.rpc('assign_task', {
        p_task_id: taskId,
        p_new_assignee_id: newAssigneeId,
        p_actor_id: userId
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Tarefa atribuída com sucesso');
      invalidateAllTasks();
    },
    onError: (error: any) => {
      console.error('Erro ao atribuir tarefa:', error);
      toast.error('Erro ao atribuir tarefa');
    }
  });

  // Create followup mutation
  const createFollowupMutation = useMutation({
    mutationFn: async ({ originalTaskId, title, description }: { originalTaskId: string; title: string; description?: string }) => {
      if (!userId) throw new Error('User not authenticated');

      const { data, error } = await supabase.rpc('create_followup', {
        p_original_task_id: originalTaskId,
        p_title: title,
        p_description: description || '',
        p_actor_id: userId
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Follow-up criado com sucesso');
      invalidateAllTasks();
    },
    onError: (error: any) => {
      console.error('Erro ao criar follow-up:', error);
      toast.error('Erro ao criar follow-up');
    }
  });

  // Legacy wrapper functions for backward compatibility
  const createTask = async (taskData: TaskFormData) => {
    return createTaskMutation.mutateAsync(taskData);
  };

  const updateTask = async (taskId: string, updates: Partial<TaskFormData>) => {
    return updateTaskMutation.mutateAsync({ taskId, updates });
  };

  const deleteTask = async (taskId: string) => {
    return deleteTaskMutation.mutateAsync(taskId);
  };

  const toggleTaskStatus = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'todo' : 'completed';
    return updateTaskMutation.mutateAsync({ taskId, updates: { status: newStatus } });
  };

  const assignTask = async (taskId: string, newAssigneeId: string) => {
    return assignTaskMutation.mutateAsync({ taskId, newAssigneeId });
  };

  const createFollowup = async (originalTaskId: string, title: string, description?: string) => {
    return createFollowupMutation.mutateAsync({ originalTaskId, title, description });
  };

  const completeTask = async (taskId: string) => {
    return completeTaskMutation.mutateAsync(taskId);
  };

  return {
    tasks,
    stats,
    loading,
    createTask,
    updateTask,
    deleteTask,
    toggleTaskStatus,
    assignTask,
    createFollowup,
    completeTask,
    refetch,
    // Expose invalidation for external use
    invalidateAllTasks
  };
};
